// Shared constants and small helpers for the Documents module UI.

import {
  FileImage, FileVideo, FileAudio, Sheet as SheetIcon, FileBarChart,
  FileType, FileArchive, FileText, Link as LinkIcon,
  Archive, CalendarClock, CheckCircle2, Clock, XCircle, ShieldAlert,
} from "lucide-react";

export const TAG_META = {
  proposal:        { label: "Proposal",         cls: "bg-sky-100 text-sky-800 border-sky-300" },
  guidelines:      { label: "Guidelines",       cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  company_policy:  { label: "Company Policy",   cls: "bg-violet-100 text-violet-800 border-violet-300" },
  other:           { label: "Other",            cls: "bg-slate-100 text-slate-800 border-slate-300" },
};

export const TAG_ORDER = ["proposal", "guidelines", "company_policy", "other"];

export const STATUS_META = {
  draft:           { label: "Draft",                    cls: "bg-slate-100 text-slate-700 border-slate-300",         Icon: Archive },
  pending_admin:   { label: "Pending Admin",            cls: "bg-amber-100 text-amber-800 border-amber-300",         Icon: Clock },
  pending_review:  { label: "Under Review",             cls: "bg-violet-100 text-violet-800 border-violet-300",      Icon: Clock },
  pending_master:  { label: "Pending Master Admin",     cls: "bg-orange-100 text-orange-800 border-orange-300",      Icon: Clock },
  scheduled:       { label: "Scheduled",                cls: "bg-amber-50 text-amber-800 border-amber-300",          Icon: CalendarClock },
  approved:        { label: "Published",                cls: "bg-emerald-100 text-emerald-800 border-emerald-300",   Icon: CheckCircle2 },
  rejected:        { label: "Rejected",                 cls: "bg-rose-100 text-rose-800 border-rose-300",            Icon: XCircle },
  expired:         { label: "Archived",                 cls: "bg-slate-100 text-slate-500 border-slate-300",         Icon: Archive },
};

// Public "shown on Documents tab" statuses only:
export const PUBLIC_CARD_STATUSES = new Set(["approved", "expired", "scheduled"]);

export function fmtSize(b) {
  if (!b) return "0 B";
  if (b > 1048576) return `${(b / 1048576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(b / 1024))} KB`;
}

export function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

/** Countdown formatter — returns "Dd HH:MM:SS" up to 99 days. */
export function fmtCountdown(secondsLeft) {
  if (secondsLeft == null) return "";
  const s = Math.max(0, Math.floor(secondsLeft));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d}d ${pad(h)}:${pad(m)}:${pad(sec)}`;
}

export function attachmentFlavor(a) {
  if (a?.type === "url") return { Icon: LinkIcon, cls: "text-sky-600 bg-sky-100" };
  const name = (a?.filename || "").toLowerCase();
  const ext = name.split(".").pop() || "";
  if (["png", "jpg", "jpeg", "gif", "webp", "heic"].includes(ext)) return { Icon: FileImage,    cls: "text-pink-600 bg-pink-100" };
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext))           return { Icon: FileVideo,    cls: "text-rose-600 bg-rose-100" };
  if (["mp3", "wav", "ogg", "flac", "aac"].includes(ext))           return { Icon: FileAudio,    cls: "text-amber-600 bg-amber-100" };
  if (["xls", "xlsx", "csv", "tsv"].includes(ext))                  return { Icon: SheetIcon,    cls: "text-emerald-600 bg-emerald-100" };
  if (["ppt", "pptx", "key"].includes(ext))                         return { Icon: FileBarChart, cls: "text-amber-600 bg-amber-100" };
  if (["zip", "rar", "tar", "gz", "7z"].includes(ext))              return { Icon: FileArchive,  cls: "text-slate-600 bg-slate-100" };
  if (["pdf"].includes(ext))                                        return { Icon: FileType,     cls: "text-rose-600 bg-rose-100" };
  return { Icon: FileText, cls: "text-indigo-600 bg-indigo-100" };
}

export const HIGHLIGHT_COLORS = [
  { key: "yellow", cls: "bg-yellow-200 text-yellow-900 border-yellow-400", swatch: "bg-yellow-300" },
  { key: "green",  cls: "bg-green-200 text-green-900 border-green-400",    swatch: "bg-green-300" },
  { key: "pink",   cls: "bg-pink-200 text-pink-900 border-pink-400",       swatch: "bg-pink-300" },
  { key: "blue",   cls: "bg-blue-200 text-blue-900 border-blue-400",       swatch: "bg-blue-300" },
  { key: "orange", cls: "bg-orange-200 text-orange-900 border-orange-400", swatch: "bg-orange-300" },
];

export const IMPORTANT_BADGE = {
  cls: "bg-rose-600 text-white border-rose-700",
  Icon: ShieldAlert,
};
